import { bungie, BungieError } from '../lib/bungie';
import { items, table } from '../lib/manifest';

/**
 * The Guardian hub's live data for one player (port of the app's HubProfile): characters, triumph scores, every
 * triumph's state, collectibles, progressions and what each character can launch. One Bungie request.
 */

export const CLASSES = ['Titan', 'Hunter', 'Warlock'];
export const RACES = ['Human', 'Awoken', 'Exo'];

export interface Player { membershipType: number; membershipId: string; name: string }

export interface HubChar {
  id: string;
  cls: string;
  classType: number;
  race: string;
  light: number;
  emblem: string;
  banner: string;
  emblemHash: number;
  lastPlayed: Date;
  minutes: number;
  titleRecord: number;
  female: boolean;
}

export interface HubActivity { hash: number; focused: boolean; modifiers: number[]; rewards: number[]; light: number }
export interface HubObjective { hash: number; progress: number; goal: number; done: boolean }

export class HubRecord {
  constructor(public state: number, public objectives: HubObjective[], public times: number, public steps: boolean) {}
  /** Done when Bungie has cleared "objective not completed" (4), or it was already redeemed (1). */
  get done() { return (this.state & 4) === 0 || (this.state & 1) !== 0; }
  get redeemed() { return (this.state & 1) !== 0; }
  get secret() { return (this.state & 8) !== 0; }
  get hidden() { return (this.state & 16) !== 0; }
  get locked() { return (this.state & 32) !== 0 && !this.done; }
  /** How far along, 0 to 1: the average of its objectives (a staged triumph: its last stage). */
  get fraction() {
    if (this.done) return 1;
    if (this.objectives.length === 0) return 0;
    const part = (o: HubObjective) => (o.done ? 1 : o.goal > 0 ? Math.min(1, Math.max(0, o.progress / o.goal)) : 0);
    return this.steps ? part(this.objectives[this.objectives.length - 1]) : this.objectives.reduce((s, o) => s + part(o), 0) / this.objectives.length;
  }
}

export interface Progress { level: number; into: number; size: number }

export class Hub {
  name = '';
  player!: Player;
  guardianRank = 0;
  chars: HubChar[] = [];
  activeScore = 0;
  legacyScore = 0;
  lifetimeScore = 0;
  hasRecords = false;
  hasCollections = false;
  season = 0;
  seasonPass = 0;
  activities: Record<string, HubActivity[]> = {};
  progressions: Record<string, Record<number, Progress>> = {};
  rawProgressions: any;
  rawChecklists: any;
  recent: number[] = [];
  metrics: Record<number, number> = {};
  time = new Date();
  private account = new Map<number, HubRecord>();
  private perChar = new Map<string, Map<number, HubRecord>>();
  private collect = new Map<number, number>();
  private collectPerChar = new Map<string, Map<number, number>>();

  /** The triumph for this character: account-wide first, then the character's own, else the furthest along of any. */
  record(hash: number, charId?: string): HubRecord | undefined {
    const a = this.account.get(hash);
    if (a) return a;
    if (charId) {
      const mine = this.perChar.get(charId)?.get(hash);
      if (mine) return mine;
    }
    let best: HubRecord | undefined;
    for (const c of this.perChar.values()) {
      const r = c.get(hash);
      if (r && (!best || r.fraction > best.fraction)) best = r;
    }
    return best;
  }

  /** A collectible for this character: 1 owned, 0 not owned, -1 the game does not show it. */
  owned(hash: number, charId?: string): number {
    let state = this.collect.get(hash) ?? (charId ? this.collectPerChar.get(charId)?.get(hash) : undefined);
    if (state === undefined)
      for (const c of this.collectPerChar.values()) {
        const s = c.get(hash);
        if (s !== undefined && (state === undefined || (s & 1) === 0)) state = s;
      }
    if (state === undefined || (state & 4) !== 0) return -1;
    return (state & 1) === 0 ? 1 : 0;
  }

  /**
   * Whether a checklist entry is ticked: account-wide lists first, then any character's own (region chests and lost
   * sectors are kept per character). Undefined when Bungie sent no such list.
   */
  ticked(checklist: number, entry: number): boolean | undefined {
    const read = (lists: any) => { const v = lists?.[checklist]?.[entry]; return v === undefined ? undefined : !!v; };
    let found = read(this.rawChecklists);
    if (found) return true;
    for (const c of Object.values<any>(this.rawProgressions ?? {})) {
      const mine = read(c?.checklists);
      if (mine) return true;
      if (mine === false) found = false;
    }
    return found;
  }

  progress(charId: string | undefined, hash: number): Progress | undefined {
    return charId ? this.progressions[charId]?.[hash] : undefined;
  }

  static async load(player: Player, token?: string): Promise<Hub> {
    const p = await bungie<any>(`/Destiny2/${player.membershipType}/Profile/${player.membershipId}/?components=100,104,200,202,204,800,900,1100`, { token });
    const info = p.profile?.data;
    const records = p.profileRecords?.data;
    const hub = new Hub();
    hub.player = player;
    hub.name = bungieName(info?.userInfo) || player.name || 'Guardian';
    hub.guardianRank = info?.currentGuardianRank ?? 0;
    hub.activeScore = records?.activeScore ?? 0;
    hub.legacyScore = records?.legacyScore ?? 0;
    hub.lifetimeScore = records?.lifetimeScore ?? 0;
    hub.hasRecords = !!records;
    hub.season = info?.currentSeasonHash ?? 0;
    hub.seasonPass = info?.currentSeasonPassHash ?? 0;
    hub.rawProgressions = p.characterProgressions?.data;
    hub.rawChecklists = p.profileProgression?.data?.checklists;

    const lite = await items();
    for (const [cid, c] of Object.entries<any>(p.characters?.data ?? {})) {
      hub.chars.push({
        id: cid,
        cls: CLASSES[c.classType] ?? 'Guardian',
        classType: c.classType,
        race: RACES[c.raceType] ?? '',
        light: c.light ?? 0,
        emblem: c.emblemPath ?? '',
        // the emblem's wide banner (the one the game stretches behind the name), else its normal one
        banner: lite[c.emblemHash]?.banner || c.emblemBackgroundPath || '',
        emblemHash: c.emblemHash ?? 0,
        lastPlayed: new Date(c.dateLastPlayed),
        minutes: Number(c.minutesPlayedTotal ?? 0),
        titleRecord: c.titleRecordHash ?? 0,
        female: c.genderType === 1,
      });
    }
    hub.chars.sort((a, b) => b.lastPlayed.getTime() - a.lastPlayed.getTime());

    const read = (from: any, into: Map<number, HubRecord>) => {
      for (const [hash, r] of Object.entries<any>(from ?? {})) {
        const staged = (r.intervalObjectives?.length ?? 0) > 0;
        const steps: any[] = staged ? r.intervalObjectives : r.objectives ?? [];
        into.set(Number(hash), new HubRecord(r.state ?? 0, steps.map((o) => {
          const progress = o.progress ?? 0, goal = o.completionValue ?? 0;
          // Bungie's own "complete" flag is set on some list-style objectives that are not done: the count decides
          return { hash: o.objectiveHash, progress, goal, done: goal > 0 ? progress >= goal : !!o.complete };
        }), r.completedCount ?? 0, staged));
      }
    };
    for (const [cid, c] of Object.entries<any>(p.characterProgressions?.data ?? {})) {
      const mine: Record<number, Progress> = (hub.progressions[cid] = {});
      for (const [hash, g] of Object.entries<any>(c.progressions ?? {}))
        mine[Number(hash)] = { level: g.level ?? 0, into: g.progressToNextLevel ?? 0, size: g.nextLevelAt ?? 0 };
    }
    for (const [cid, c] of Object.entries<any>(p.characterActivities?.data ?? {}))
      hub.activities[cid] = (c.availableActivities ?? []).filter((a: any) => a.isVisible !== false && a.activityHash).map((a: any) => ({
        hash: a.activityHash,
        focused: !!a.isFocusedActivity,
        modifiers: (a.modifierHashes ?? []).filter(Boolean),
        rewards: [...new Set<number>((a.visibleRewards ?? []).flatMap((r: any) => r.rewardItems ?? []).map((i: any) => i.itemQuantity?.itemHash).filter(Boolean))],
        light: a.recommendedLight ?? 0,
      }));
    for (const [mh, m] of Object.entries<any>(p.metrics?.data?.metrics ?? {}))
      if (m.objectiveProgress) hub.metrics[Number(mh)] = m.objectiveProgress.progress ?? 0;
    hub.hasCollections = !!p.profileCollectibles?.data;
    for (const [h, c] of Object.entries<any>(p.profileCollectibles?.data?.collectibles ?? {})) hub.collect.set(Number(h), c.state ?? 0);
    hub.recent = p.profileCollectibles?.data?.recentCollectibleHashes ?? [];
    for (const [cid, c] of Object.entries<any>(p.characterCollectibles?.data ?? {})) {
      const m = new Map<number, number>();
      for (const [h, x] of Object.entries<any>(c.collectibles ?? {})) m.set(Number(h), x.state ?? 0);
      hub.collectPerChar.set(cid, m);
    }
    read(records?.records, hub.account);
    for (const [cid, c] of Object.entries<any>(p.characterRecords?.data ?? {})) {
      const m = new Map<number, HubRecord>();
      read(c.records, m);
      hub.perChar.set(cid, m);
    }
    return hub;
  }
}

export function bungieName(u: any): string {
  if (!u) return '';
  return u.bungieGlobalDisplayName ? `${u.bungieGlobalDisplayName}#${String(u.bungieGlobalDisplayNameCode ?? 0).padStart(4, '0')}` : u.displayName ?? '';
}

// ---------------- finding the player ----------------

/** The signed-in player's Destiny account: the one Cross Save points at, else Bungie's primary one, else the first. */
export async function signedInPlayer(token: string): Promise<Player> {
  const m = await bungie<any>('/User/GetMembershipsForCurrentUser/', { token });
  const list: any[] = m.destinyMemberships ?? [];
  const pick = list.find((x) => x.crossSaveOverride !== 0 && x.crossSaveOverride === x.membershipType)
    ?? list.find((x) => x.membershipId === m.primaryMembershipId) ?? list[0];
  if (!pick) throw new BungieError(0, 'NoDestiny', 'This Bungie account has no Destiny 2 characters yet.');
  return { membershipType: pick.crossSaveOverride || pick.membershipType, membershipId: pick.membershipId, name: bungieName(pick) };
}

/** A player by Bungie name ("Name#1234"), as the app's Tracker setting takes it. */
export async function findPlayer(text: string): Promise<Player> {
  const m = /^(.+)#(\d{1,4})$/.exec(text.trim());
  if (!m) throw new BungieError(0, 'BadName', 'Enter a Bungie name with its number, like Name#1234.');
  const hits = await bungie<any[]>('/Destiny2/SearchDestinyPlayerByBungieName/-1/', {
    method: 'POST',
    body: { displayName: m[1].trim(), displayNameCode: Number(m[2]) },
  });
  const pick = hits.find((h) => h.crossSaveOverride === 0 || h.membershipType === h.crossSaveOverride);
  if (!pick) throw new BungieError(0, 'NotFound', `No Destiny 2 player called '${text.trim()}' was found.`);
  return { membershipType: pick.membershipType, membershipId: pick.membershipId, name: bungieName(pick) };
}

/** The guardian rank's name ("Rank 9: Paragon"...). */
export async function rankName(rank: number): Promise<string> {
  if (!rank) return '';
  const t = await table<any>('DestinyGuardianRankDefinition');
  return Object.values<any>(t).find((r) => r.rankNumber === rank)?.displayProperties?.name ?? '';
}
